import React from 'react';
import {
  AlertCircle,
  CheckCircle,
  Download,
  Loader2,
} from 'lucide-react';

import { BackgroundJobProgressDTO } from '../../types/backgroundjobs/BackgroundJobTypes';

interface BackgroundJobCardProps {
  job: BackgroundJobProgressDTO;
  onDownload?: (uuid: string) => void;
  isDownloading?: boolean;
  downloadProgress?: number | null;
}

const jobTypeLabel: Record<string, string> = {
  SALE_DTE_BULK_DOWNLOAD: 'Descarga masiva DTE',
  REPORT_EXPORT: 'Exportación de reporte',
  INVENTORY_IMPORT: 'Importación de inventario',
  RESTAURANT_REPORT_EXPORT: 'Reporte de restaurante',
};

const BackgroundJobCard: React.FC<BackgroundJobCardProps> = ({
  job,
  onDownload,
  isDownloading = false,
  downloadProgress = null,
}) => {
  const rawProgress =
    job.totalItems > 0
      ? Math.round(
          (job.processedItems / job.totalItems) * 100
        )
      : job.status === 'COMPLETED'
      ? 100
      : 0;

  const progress = Math.min(
    Math.max(rawProgress, 0),
    100
  );

  const normalizedDownloadProgress =
    downloadProgress === null
      ? null
      : Math.min(
          Math.max(downloadProgress, 0),
          100
        );

  const isPending = job.status === 'PENDING';
  const isProcessing = job.status === 'PROCESSING';
  const isCompleted = job.status === 'COMPLETED';
  const isFailed = job.status === 'FAILED';
  const isExpired = job.status === 'EXPIRED';

  const title =
    jobTypeLabel[job.jobType]
    || 'Tarea en segundo plano';

  const description = isFailed
    ? job.errorMessage || 'La tarea falló.'
    : isExpired
    ? 'La tarea expiró.'
    : job.fileName || 'Procesando tarea...';

  const statusLabel = isPending
    ? 'Pendiente'
    : isProcessing
    ? 'Procesando'
    : isCompleted
    ? 'Archivo listo'
    : isFailed
    ? 'Falló'
    : 'Expirado';

  const statusClass = isCompleted
    ? 'text-success'
    : isFailed || isExpired
    ? 'text-danger'
    : isPending
    ? 'text-warning'
    : 'text-primary';

  const barClass = isCompleted
    ? 'bg-success'
    : isFailed || isExpired
    ? 'bg-danger'
    : isPending
    ? 'bg-warning'
    : 'bg-primary';

  return (
    <div className="rounded-sm border border-stroke bg-gray-2 p-4 dark:border-strokedark dark:bg-meta-4">
      <div className="flex items-start gap-3">
        <div
          className={`
            rounded-sm bg-white p-2
            ${statusClass}
            dark:bg-boxdark
          `}
        >
          {isCompleted ? (
            <CheckCircle className="h-5 w-5" />
          ) : isFailed || isExpired ? (
            <AlertCircle className="h-5 w-5" />
          ) : (
            <Loader2 className="h-5 w-5 animate-spin" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-black dark:text-white">
            {title}
          </h3>

          <p className="mt-1 break-all text-xs text-body dark:text-bodydark">
            {description}
          </p>

          <div className="mt-3 h-2 overflow-hidden rounded-full bg-white dark:bg-boxdark">
            <div
              className={`
                h-full rounded-full transition-all duration-300
                ${barClass}
              `}
              style={{
                width: `${progress}%`,
              }}
            />
          </div>

          <div className="mt-2 flex items-center justify-between gap-3 text-xs">
            <span className="text-body dark:text-bodydark">
              {isCompleted
                ? `${progress}% generado`
                : `${progress}% completado`}
            </span>

            <span
              className={`
                flex items-center gap-1
                ${statusClass}
              `}
            >
              {(isPending || isProcessing) && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              )}

              {statusLabel}
            </span>
          </div>

          {isCompleted && onDownload && (
            <div className="mt-3">
              {isDownloading ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3 text-xs">
                    <span className="flex items-center gap-1.5 text-primary">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />

                      Descargando archivo...
                    </span>

                    {normalizedDownloadProgress !== null && (
                      <span className="font-medium text-primary">
                        {normalizedDownloadProgress}%
                      </span>
                    )}
                  </div>

                  {normalizedDownloadProgress !== null && (
                    <div className="h-1.5 overflow-hidden rounded-full bg-white dark:bg-boxdark">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-300"
                        style={{
                          width: `${normalizedDownloadProgress}%`,
                        }}
                      />
                    </div>
                  )}

                  {normalizedDownloadProgress === null && (
                    <p className="text-xs text-body dark:text-bodydark">
                      Preparando la transferencia del archivo...
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex justify-end text-xs">
                  <button
                    type="button"
                    onClick={() => onDownload(job.uuid)}
                    className="
                      flex items-center gap-1
                      text-primary transition
                      hover:underline
                    "
                  >
                    <Download className="h-3.5 w-3.5" />

                    Descargar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackgroundJobCard;